// src/Main/Sectiondetails.js
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/api';
import { Doughnut } from 'react-chartjs-2';
import debounce from 'lodash/debounce';
import './Sectiondetails.css';
import AnalyticsSection from '../../components/analytics/AnalyticsSection';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, DoughnutController);

const formatCurrency = (value) => {
  if (typeof value !== 'number' || isNaN(value)) return '';
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ').replace('.', ',');
};

function calculateMonthlyPayment(principal, months, rate) {
  const monthlyRate = rate / 100 / 12;
  if (monthlyRate === 0) return principal / months;
  return (
    principal *
    (monthlyRate * Math.pow(1 + monthlyRate, months)) /
    (Math.pow(1 + monthlyRate, months) - 1)
  );
}

function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

/**
 * Массивы полей для каждой вкладки (раздела).
 * Здесь описаны все поля, которые должны присутствовать в каждом разделе.
 */
const fieldsBySection = {
  income: [
    { id: 'salary', label: 'Зарплата', placeholder: '000,00 руб.' },
    { id: 'sideIncome', label: 'Подработка', placeholder: '000,00 руб.' },
    { id: 'interests', label: 'Доход по вкладам', placeholder: '000,00 руб.' },
    { id: 'subsidies', label: 'Субсидии/пособия', placeholder: '000,00 руб.' },
    { id: 'revenue', label: 'Аренда недвижимости', placeholder: '000,00 руб.' },
    { id: 'investments', label: 'Доход по инвестициям', placeholder: '000,00 руб.' },
    { id: 'otherIncome', label: 'Разное', placeholder: '000,00 руб.' },
    { id: 'incomegrowth', label: 'Рост доходов в год, %', placeholder: '%' },
  ],
  expenses: [
    { id: 'meals', label: 'Питание', placeholder: '000,00 руб.' },
    { id: 'rental', label: 'Аренда недвижимости', placeholder: '000,00 руб.' },
    { id: 'utility', label: 'ЖКХ', placeholder: '000,00 руб.' },
    { id: 'transportation', label: 'Транспорт', placeholder: '000,00 руб.' },
    { id: 'clothing', label: 'Одежда', placeholder: '000,00 руб.' },
    { id: 'education', label: 'Обучение', placeholder: '000,00 руб.' },
    { id: 'telecom', label: 'Связь/Интернет', placeholder: '000,00 руб.' },
    { id: 'entertainment', label: 'Развлечения', placeholder: '000,00 руб.' },
    { id: 'medical', label: 'Медицина', placeholder: '000,00 руб.' },
    { id: 'relatives', label: 'Помощь родственникам', placeholder: '000,00 руб.' },
    { id: 'other', label: 'Разное', placeholder: '000,00 руб.' },
    { id: 'outcomegrowth', label: 'Рост расходов в год, %', placeholder: '%' },
  ],
  yearlyExpenses: [
    { id: 'vacation', label: 'Отпуск (годовой)', placeholder: '000,00 руб.' },
    { id: 'insurance', label: 'Страховка', placeholder: '000,00 руб.' },
    { id: 'bigPurchase1', label: 'Крупная покупка', placeholder: '000,00 руб.' },
    { id: 'bigPurchase2', label: 'Крупная покупка', placeholder: '000,00 руб.' },
    { id: 'bigPurchase3', label: 'Крупная покупка', placeholder: '000,00 руб.' },
  ],
  assets: [
    { id: 'accumulation', label: 'Наличные', placeholder: '000,00 руб.' },
    { id: 'crypto', label: 'Криптоактивы', placeholder: '000,00 руб.' },
    { id: 'stock', label: 'Инвестиции (акции и т.д.)', placeholder: '000,00 руб.' },
    { id: 'bankaccaunt', label: 'Банковские вклады', placeholder: '000,00 руб.' },
    { id: 'apartment', label: 'Квартира', placeholder: '000,00 руб.' },
    { id: 'car', label: 'Транспорт (авто/мото)', placeholder: '000,00 руб.' },

  ],
  reserve: [
    { id: 'accumulations', label: 'Наличные', placeholder: '000,00 руб.' },
    { id: 'cryptos', label: 'Криптоактивы', placeholder: '000,00 руб.' },
    { id: 'stocks', label: 'Инвестиции (акции и т.д.)', placeholder: '000,00 руб.' },
    { id: 'bankaccaunts', label: 'Банковские вклады', placeholder: '000,00 руб.' },
    { id: 'apartments', label: 'Квартира', placeholder: '000,00 руб.' },
    { id: 'cars', label: 'Транспорт (авто/мото)', placeholder: '000,00 руб.' },
    { id: 'business', label: 'Бизнес', placeholder: '000,00 руб.' },
  ],
  flat: [
    { id: 'price', label: 'Стоимость квартиры', placeholder: '000,00 руб.' },
    { id: 'apartment', label: 'Первоначальный взнос', placeholder: '000,00 руб.' },
    { id: 'consulting', label: 'Сопровождение сделки', placeholder: '000,00 руб.' },
    { id: 'repair', label: 'Ремонт', placeholder: '000,00 руб.' },
    { id: 'credit', label: 'Размер кредита', placeholder: '000,00 руб.' },
    { id: 'bankaccaunts', label: 'Процентная ставка', placeholder: '%' },
    { id: 'period', label: 'Срок кредита', placeholder: 'мес.' },
    { id: 'monthly', label: 'Ежемесячный взнос', placeholder: '000,00 руб.' },
    { id: 'bodycredit', label: 'Тело кредита в месяц', placeholder: '000,00 руб.' },
    { id: 'percentcredit', label: 'Сумма % в месяц', placeholder: '000,00 руб.' },
    { id: 'totalbodycredit', label: 'Сумма всего тела кредита', placeholder: '000,00 руб.' },
    { id: 'sumpercent', label: 'Сумма % за весь срок', placeholder: '000,00 руб.' },
    { id: 'total', label: 'Сумма выплат за весь срок', placeholder: '000,00 руб.' },
  ],

  rent: [
    { id: 'rent', label: 'Аренда квартиры', placeholder: '000,00 руб.' },
    { id: 'utility', label: 'ЖКХ', placeholder: '000,00 руб.' },
    { id: 'rentgrowth', label: 'Рост аренды в год', placeholder: '%' },
  ],
};

/**
 * Тексты заголовка и описания для каждой вкладки
 */
const sectionInfo = {
  income: {
    title: 'Доходы',
    description:
      'Внесите все доходы (после уплаты налогов), которые Вы получаете в среднем в месяц. Это позволит определить наличие средств для обслуживания кредита относительно Ваших расходов.',
  },
  expenses: {
    title: 'Расходы',
    description:
      'Внесите все ежемесячные расходы. Это позволит сравнить расходы с доходами и определить возможность обслуживания кредита.',
  },
  yearlyExpenses: {
    title: 'Годовые расходы',
    description:
      'Внесите крупные годовые затраты, такие как отпуск, страховка, крупные покупки и другие расходы, которые происходят раз в год.',
  },
  assets: {
    title: 'Активы',
    description:
      'Внесите активы, которыми располагаете и которые можно использовать в качестве первоначального взноса по кредиту (в том числе продать авто или квартиру, если нужно больше наличных). Средства для "Подушки безопасности" находятся в разделе "Резервы".',
  },
  reserve: {
    title: 'Резервы',
    description:
      'Здесь Вы можете определить резервы в качестве "подушки безопасности", долгосрочных инвестиций и сбережений.',
  },
  flat: {
    title: 'Квартира в кредит',
    description:
      'Данный раздел учитывает банковские условия на кредит (ипотеку), а также затраты на сопровождение сделки (услуги риэлторов и т.д.). Обратите внимание, что в этом разделе не отображается итоговая сумма и диаграмма – здесь вводятся только расчётные параметры.',
  },
};

/**
 * Создаём начальное состояние для всех вкладок сразу,
 * чтобы каждая вкладка начинала с нулевых значений.
 */
const createInitialState = () => {
  const initialState = {};
  Object.keys(fieldsBySection).forEach((sectionKey) => {
    initialState[sectionKey] = {};
    fieldsBySection[sectionKey].forEach((field) => {
      initialState[sectionKey][field.id] = 0;
    });
  });
  return initialState;
};

const Sectiondetails = ({ activeSection }) => {
  // Храним все данные всех вкладок в одном состоянии
  const [allSectionsData, setAllSectionsData] = useState(() => createInitialState());
  const [rawInputs, setRawInputs] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (activeSection !== 'flat') return;
  
    const flatData = allSectionsData.flat || {};
    const price = flatData.price || 0;
    const initial = flatData.apartment || 0;
    const consulting = flatData.consulting || 0;
    const repair = flatData.repair || 0;
    const rate = flatData.bankaccaunts || 0;
    const period = flatData.period || 0;
  
    const credit = price - initial + consulting + repair;
  
    if (!credit || !rate || !period) return;
  
    const monthly = calculateMonthlyPayment(credit, period, rate);
    const total = monthly * period;
    const sumPercent = total - credit;
    const bodyCredit = credit / period;
    const percentCredit = monthly - bodyCredit;
    const totalBodyCredit = bodyCredit * period;

    const updated = {
      ...flatData,
      credit,
      monthly,
      total,
      sumpercent: sumPercent,
      bodycredit: bodyCredit,
      percentcredit: percentCredit,
      totalbodycredit: totalBodyCredit,
    };
  
    // Если данные не изменились, не обновляем состояние
    if (JSON.stringify(flatData) === JSON.stringify(updated)) return;
  
    setAllSectionsData((prev) => ({
      ...prev,
      flat: updated,
    }));
  
    saveData('flat', updated);
  }, [activeSection, allSectionsData.flat]);

  // Загружаем сохранённые данные с сервера при смене вкладки
  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));

        const token = user?.accessToken;
        if (!token) return;
  
        const response = await fetchWithAuth('/api/finance/allData');
          
        const result = await response.json();
        if (result.data) {
          const merged = { ...createInitialState() };
  
          for (const sectionKey in result.data) {
            merged[sectionKey] = {
              ...merged[sectionKey],
              ...result.data[sectionKey],
            };
          }
  
          setAllSectionsData(merged);
          setIsLoading(false);

          console.log('📦 Загруженные данные в allSectionsData:', merged);

        }
      } catch (error) {
        console.error('Ошибка при загрузке данных с сервера:', error);
        setIsLoading(false);
      }
    };
  
    fetchData();
  }, []);
  
  // Поля для текущего раздела (вкладки)
  const currentFields = fieldsBySection[activeSection] || [];
  const { title, description } = sectionInfo[activeSection] || { title: '', description: '' };

  // Debounce-сохранение данных на сервер
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Debounce‑сохранение данных на сервер
  const saveData = useCallback(
    debounce((sectionKey, data) => {
      fetchWithAuth(`/api/finance/${sectionKey}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
        .then(async (res) => {
          const text = await res.text();
          try {
            const json = JSON.parse(text);
            console.log(`✅ Ответ от сервера [${sectionKey}]:`, json);
          } catch (err) {
            console.warn('⚠️ Сервер вернул не‑JSON:', text);
          }
        })
        .catch((error) => {
          console.error('❌ Ошибка при сохранении данных:', error);
        });
    }, 800),
    []
  );

  
  // Обработчик ввода для текущей вкладки
  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    // Сохраняем введённое значение как есть
    setRawInputs((prev) => ({ ...prev, [name]: value }));
  
    // Парсим в число: убираем пробелы и заменяем запятую на точку
    const raw = value.replace(/\s/g, '').replace(',', '.');
    const numericValue = parseFloat(raw) || 0;
  
    setAllSectionsData((prevState) => {
      const newState = { ...prevState };
      newState[activeSection] = {
        ...prevState[activeSection],
        [name]: numericValue,
      };
      saveData(activeSection, newState[activeSection]);
      return newState;
    });
  };

  const handleClearSection = () => {
    const clearedData = {};
    fieldsBySection[activeSection].forEach((field) => {
      clearedData[field.id] = 0;
    });
  
    setAllSectionsData((prev) => ({
      ...prev,
      [activeSection]: clearedData,
    }));
  
    saveData(activeSection, clearedData);
  };

  // Данные текущего раздела (форма)
  const formData = useMemo(() => {
    return allSectionsData[activeSection] || {};
  }, [allSectionsData, activeSection]);


  const handleInputBlur = (e) => {
    const { name } = e.target;
    setRawInputs((prev) => {
      const updated = { ...prev };
      delete updated[name]; // После выхода из поля — снова отформатированное значение
      return updated;
    });
  };
  
  // Вычисляем итоговую сумму (исключая поля с процентами, периодами и др.)
  const totalAmount = useMemo(() => {
    return Object.entries(formData).reduce((acc, [key, val]) => {
      if (
        key.toLowerCase().includes('growth') ||
        key.toLowerCase().includes('percent') ||
        key.toLowerCase().includes('period') ||
        key === 'bankaccaunts'
      ) {
        return acc;
      }
      return acc + (typeof val === 'number' ? val : 0);
    }, 0);
  }, [formData]);

  // Формируем данные для диаграммы:
  // Берём поля, которые действительно числовые (без символов '%' или 'мес')
  const numericFields = currentFields.filter(
    (field) => !field.placeholder.includes('%') && !field.placeholder.includes('мес')
  );
  const chartLabels = numericFields.map((field) => field.label);
  const chartValues = numericFields.map((field) => formData[field.id] || 0);

  // Задаём наборы цветов для диаграммы в зависимости от раздела
  let backgroundColors = [];
  switch (activeSection) {
    case 'income':
    case 'expenses':
    case 'assets':
    case 'reserve':
    case 'yearlyExpenses':
      backgroundColors = ['#FF6384', '#36A2EB', '#FFCE56', '#FF9F40', '#00A86B', '#4BC0C0', '#9966FF'];
      break;
    default:
      backgroundColors = ['#FF6384', '#36A2EB', '#FFCE56', '#FF9F40', '#00A86B', '#4BC0C0', '#9966FF'];
      break;
  }
  const hoverColors = backgroundColors;

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        data: chartValues,
        backgroundColor: backgroundColors.slice(0, chartLabels.length),
        hoverBackgroundColor: hoverColors.slice(0, chartLabels.length),
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '85%',
    borderRadius: 20,
    layout: {
      padding: {
        top: 20, bottom: 20, left: 0, right: 0 // добавляем отступы вокруг диаграммы
      }
    },
    plugins: {
      legend: {
        position: 'right', // легенда остаётся справа
        align: 'start',     // легенда выравнивается по верхнему краю
        labels: {
          color: 'black',
          usePointStyle: true,
          pointStyle: 'rectRounded',
          boxWidth: 20,
          font: { size: 16, style: 'normal', weight: 'normal' },
          padding: 8, // небольшой отступ между пунктами легенды
        },
      },
      datalabels: {
        display: false // <-- Отключаем подписи на дугах
      },
    },
  };

  console.log('🧾 formData для', activeSection, ':', formData);

  if (isLoading) {
    return <div className="sectiondetailsContainer"><p>Загрузка данных...</p></div>;
  }  

  if (activeSection === 'analytics') {
    console.log("Данные для аналитики:", allSectionsData);
    return <AnalyticsSection data={allSectionsData} />;
  }

  return (
    <div className="sectiondetailsContainer">
      <h2>{title}</h2>
      <p>{description}</p>
      <div className="inputFieldsContainer">
        {currentFields.map((field, index) => (
          <div key={index} className="inputField">
            <label htmlFor={field.id}>{field.label}</label>
            <input
              type="text"
              id={field.id}
              name={field.id}
              placeholder={field.placeholder}              
              value={
                rawInputs[field.id] !== undefined
                  ? rawInputs[field.id]
                  : typeof formData[field.id] === 'number' && formData[field.id] !== 0
                    ? formatCurrency(formData[field.id])
                    : ''
              }
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              title="Введите сумму в рублях (при необходимости используйте точку для копеек)"
              maxLength="15"
            />
          </div>
        ))}
      </div>


      {activeSection !== 'flat' ? (
        <>
          {/* Блок ИТОГО */}
          <div className="total-block">
            <h2 className="total-label">Итого, руб.</h2>
            <span className="total-amount">{formatCurrency(totalAmount)}</span>
          </div>

          {/* Блок диаграммы */}
          <div className="chart-container">
            <div className="canvas-container">
              <Doughnut data={chartData} options={chartOptions} />
            </div>
          </div>

          {/* Кнопка очистки */}
          <div className="clear-button-container">
            <button className="clear-section-button" onClick={handleClearSection}>
              Очистить раздел
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Диаграмма 1: Ежемесячный платёж */}
          <div className="chart-container">
            <h3>Структура ежемесячного платежа</h3>
            <div className="canvas-container">

            <Doughnut

            data={{
              labels: ['Тело кредита', 'Проценты в месяц', 'Ежемесячный взнос'],
              datasets: [
                {
                  label: 'Ежемесячный взнос',
                  data: [formData.monthly || 0],
                  backgroundColor: ['#4BC0C0'],
                  hoverBackgroundColor: ['#4BC0C0'],
                  borderRadius: 20,
                  cutout: '73%',  // внутренний радиус, чтобы сделать кольцо
                  circumference: 360,
                  rotation: -90,
                },
                {
                  label: 'Структура взноса',
                  data: [formData.bodycredit || 0, formData.percentcredit || 0],
                  backgroundColor: ['#36A2EB', '#FF6384'],
                  hoverBackgroundColor: ['#36A2EB', '#FF6384'],
                  borderRadius: 20,
                  cutout: '70%',
                  circumference: 360,
                  rotation: -90,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              layout: {
                padding: { top: 20, bottom: 20 },
              },
              plugins: {
                legend: {
                  position: 'right',
                  align: 'center',
                  labels: {
                    usePointStyle: true,
                    pointStyle: 'rectRounded',
                    boxWidth: 16,
                    font: { size: 16 },
                    padding: 10,
                    generateLabels: function (chart) {
                      // 🔄 создаём корректную легенду вручную
                      return [
                        {
                          text: 'Тело кредита',
                          fillStyle: '#36A2EB',
                          strokeStyle: '#36A2EB',
                          pointStyle: 'rectRounded',
                        },
                        {
                          text: 'Проценты',
                          fillStyle: '#FF6384',
                          strokeStyle: '#FF6384',
                          pointStyle: 'rectRounded',
                        },
                        {
                          text: 'Выплата в мес.',
                          fillStyle: '#4BC0C0',
                          strokeStyle: '#4BC0C0',
                          pointStyle: 'rectRounded',
                        },
                      ];
                    },
                  },
                },
                datalabels: {
                  display: false,
                },
              },
            }}
            />
            </div>
          </div>
          {/* Диаграмма 2: Общая сумма выплат */}
          <div className="chart-container">
            <h3>Общая структура выплат за весь срок кредита</h3>
            <div className="canvas-container">

            <Doughnut
              data={{
                labels: ['Сумма тела кредита', 'Проценты за весь срок', 'Сумма выплат за весь срок'],
                datasets: [
                  {
                    label: 'Сумма выплат за весь срок',
                    data: [formData.total || 0],
                    backgroundColor: ['#4BC0C0'],
                    hoverBackgroundColor: ['#4BC0C0'],
                    borderRadius: 20,
                    cutout: '73%',
                    circumference: 360,
                    rotation: -90,
                  },
                  {
                    label: 'Структура выплаты',
                    data: [
                      formData.totalbodycredit || 0,
                      formData.sumpercent || 0,
                    ],
                    backgroundColor: ['#36A2EB', '#FF6384'],
                    hoverBackgroundColor: ['#36A2EB', '#FF6384'],
                    borderRadius: 20,
                    cutout: '70%',
                    circumference: 360,
                    rotation: -90,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                  padding: { top: 20, bottom: 20 },
                },
                plugins: {
                  legend: {
                    position: 'right',
                    align: 'center',
                    labels: {
                      usePointStyle: true,
                      pointStyle: 'rectRounded',
                      boxWidth: 16,
                      font: { size: 16 },
                      padding: 10,
                      generateLabels: function () {
                        return [
                          {
                            text: 'Тело кредита',
                            fillStyle: '#36A2EB',
                            strokeStyle: '#36A2EB',
                            pointStyle: 'rectRounded',
                          },
                          {
                            text: 'Проценты',
                            fillStyle: '#FF6384',
                            strokeStyle: '#FF6384',
                            pointStyle: 'rectRounded',
                          },
                          {
                            text: 'Выплаты итого',
                            fillStyle: '#4BC0C0',
                            strokeStyle: '#4BC0C0',
                            pointStyle: 'rectRounded',
                          },
                        ];
                      },
                    },
                  },
                  datalabels: {
                    display: false,
                  },
                },
              }}
            />
            </div>
          </div>
          {/* Кнопка очистки */}
          <div className="clear-button-container">
            <button className="clear-section-button" onClick={handleClearSection}>
              Очистить раздел
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Sectiondetails;